import { watch, type WatchOptions, type WatchSource, type WatchStopHandle } from 'vue';

type UnwrapSource<T> = T extends WatchSource<infer V>
	? V
	: T extends object ? T : never;

type Changes<T> = Partial<{
	[K in keyof T]: {
		old: UnwrapSource<T[K]> | undefined;
		new: UnwrapSource<T[K]>
	}
}>;

/**
 * useWatch — расширенный `watch` с измененным callback
 * - принимает объект с реактивными свойствами, точно такими же, как при указании в массиве в стандартном `watch`
 * - callback возвращает изменившиеся свойства в объекте `changes` с `{old, new}`
 *
 * Если у вас есть несколько `watch`, которые меняют одно состояние, то могут возникнуть проблемы:
 * - рост длины цепочек `watch`, когда один `watch` приводит к срабатыванию другого
 * - лишние вызовы при согласованных состояниях, когда разные `watch` устанавливают одно и то же значение
 * - гонка при несогласованных состояниях, когда разные `watch` в разные тики при этом одновременно могут устанавливать разные состояния
 *
 * Если у вас такой случай и его нельзя упростить, то используйте `useWatch`, в остальных случаях используйте только стандартный `watch`
 *
 * @example
 *
 * ```js
 *
 * useWatch({ a: ref(5), b: ref('word') }, (changes) => {
 * 	console.log(changes.a?.new, changes.a?.old);
 * 	console.log(changes.b?.new, changes.b?.old);
 * });
 * ```
 */
export function useWatch<T extends Record<string, WatchSource>>(
	sourcesObj: T,
	cb: (
		changes: Changes<T>,
		onCleanup: (fn: () => void) => void,
	) => void | Promise<void>,
	options?: WatchOptions,
): WatchStopHandle {
	const keys = Object.keys(sourcesObj) as (keyof T)[];
	const sources = keys.map(k => sourcesObj[k]);

	return watch(sources, (newValues, oldValues, onCleanup) => {
		const changed = {} as Changes<T>;

		newValues.forEach((val, i) => {
			if (!Object.is(val, oldValues[i])) {
				const k = keys[i];

				changed[k] = {
					old: oldValues[i],
					new: val,
				} as any;
			}
		});

		if (Object.keys(changed).length) cb(changed, onCleanup);
	}, options);
}
